import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAssetListConfig } from '../../hooks/useAssetListConfig';
import AssetManagerService from '../../services/AssetManager/assetManager.service';
import type { AssetListConfig, AssetSelection, AssetStatistic } from '../../types/InfrastructureManagerTypes';

// Holds the settled search only; raw input stays in the search box, so the value never changes per keystroke.
interface AssetOverviewContextValue {
  config: AssetListConfig | undefined;
  isLoadingConfig: boolean;
  isConfigLoadingError: boolean;
  search: string;
  setSearch: (search: string) => void;
  selected: AssetSelection | null;
  toggleSelect: (asset: AssetSelection) => void;
  activeStatisticId: number | null;
  // The active statistic's layer and conditions; undefined while none is active, loaded, or valid.
  activeStatistic: AssetStatistic | undefined;
  toggleStatistic: (id: number) => void;
  clearStatistic: () => void;
}

const AssetOverviewContext = createContext<AssetOverviewContextValue | null>(null);

const isSameAsset = (a: AssetSelection, b: AssetSelection) => a.layerId === b.layerId && a.keyValue === b.keyValue;

const AssetOverviewProvider = ({ children }: { children: React.ReactNode }) => {
  const { config, isLoadingConfig, isConfigLoadingError } = useAssetListConfig();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AssetSelection | null>(null);
  // Only one statistic filter is active at a time; selecting it again clears it.
  const [activeStatisticId, setActiveStatisticId] = useState<number | null>(null);
  const { getStatistics } = AssetManagerService();
  const { data: statisticsData } = useQuery(getStatistics());
  const activeStatistic = useMemo(
    () => statisticsData?.statistics.find(({ id, error }) => id === activeStatisticId && !error),
    [statisticsData, activeStatisticId],
  );

  const toggleSelect = useCallback((asset: AssetSelection) => {
    setSelected((current) => (current && isSameAsset(current, asset) ? null : asset));
  }, []);

  // Switching the statistic replaces the dataset, so the selection made under the old one is dropped.
  const toggleStatistic = useCallback((id: number) => {
    setActiveStatisticId((current) => (current === id ? null : id));
    setSelected(null);
  }, []);

  const clearStatistic = useCallback(() => {
    setActiveStatisticId(null);
    setSelected(null);
  }, []);

  // A saved config replaces the dataset, so nothing selected under the old one survives.
  const configVersion = config?.updatedAt ?? null;
  const configVersionRef = useRef(configVersion);
  useEffect(() => {
    if (configVersionRef.current === configVersion) return;
    configVersionRef.current = configVersion;
    setSelected(null);
  }, [configVersion]);

  // A search the selection no longer matches drops it (same contains rule as the backend).
  useEffect(() => {
    const term = search.toLowerCase();
    if (term) setSelected((current) => (current && !current.displayValue.toLowerCase().includes(term) ? null : current));
  }, [search]);

  const value = useMemo(
    () => ({
      config,
      isLoadingConfig,
      isConfigLoadingError,
      search,
      setSearch,
      selected,
      toggleSelect,
      activeStatisticId,
      activeStatistic,
      toggleStatistic,
      clearStatistic,
    }),
    [config, isLoadingConfig, isConfigLoadingError, search, selected, toggleSelect, activeStatisticId, activeStatistic, toggleStatistic, clearStatistic],
  );

  return <AssetOverviewContext.Provider value={value}>{children}</AssetOverviewContext.Provider>;
};

const useAssetOverview = () => {
  const context = useContext(AssetOverviewContext);
  if (!context) throw new Error('useAssetOverview must be used within AssetOverviewProvider');
  return context;
};

export { AssetOverviewProvider, useAssetOverview };
